import { ChartCardConfig } from '../models/chart-config.model';

/**
 * Unified change type for all card configuration mutations.
 * All changes (chart type, AI patch, data tab, custom JSON) use this single type.
 */
export interface ConfigChange {
	type: 'patch';
	origin: ConfigChangeOrigin;
	patch: Partial<ChartCardConfig>;
}

export type ConfigChangeOrigin = 'form' | 'ai' | 'host' | 'undo' | 'runtime';

/**
 * Severity levels for validation warnings.
 */
export enum WarningSeverity {
	/** Silent badge indicator only */
	Silent = 'silent',
	/** Toast notification */
	Info = 'info',
	/** Warning banner in UI */
	Warning = 'warning',
	/** Blocking modal - user must resolve */
	Blocking = 'blocking',
}

/**
 * A validation warning for user feedback.
 */
export interface ValidationWarning {
	/** Machine-readable warning code */
	code: string;
	/** Human-readable message */
	message: string;
	/** Severity level */
	severity: WarningSeverity;
	/** Optional: field path related to warning (e.g., 'data.metrics') */
	field?: string;
	/** Optional: action user can take */
	suggestedAction?: string;
}

/**
 * Result of applying a configuration change.
 */
export interface ChangeResult {
	/** The new configuration after applying the change */
	config: ChartCardConfig;
	/** Validation warnings (empty if no issues) */
	warnings: ValidationWarning[];
	/** Whether any data migration occurred */
	migrated: boolean;
}

/**
 * Data migration result for chart type switches.
 */
export interface DataMigrationResult {
	/** Whether migration was successful */
	success: boolean;
	/** The migrated data configuration */
	data?: ChartCardConfig['data'];
	/** Warnings about data loss or changes */
	warnings: ValidationWarning[];
}

/**
 * Chart type requirements for data validation.
 */
export interface ChartTypeRequirements {
	/** Whether this chart type uses time series */
	timeSeries: boolean;
	/** Whether this chart type uses category dimension */
	usesCategory: boolean;
	/** Minimum number of metrics required */
	minMetrics: number;
	/** Maximum number of metrics allowed (undefined = no limit) */
	maxMetrics?: number;
	/** Whether dimensions are supported */
	supportsDimensions: boolean;
}

/**
 * Map of chart types to their data requirements.
 * Used for validation and migration decisions.
 */
/** User-facing labels for chart-type change snackbars and validation copy. */
export const CHART_TYPE_LABELS: Record<string, string> = {
	line: 'Line chart',
	area: 'Area chart',
	bar: 'Bar chart',
	column: 'Column chart',
	donut: 'Donut chart',
	scatter: 'Scatter chart',
	number: 'Number',
};

export function chartTypeLabel(chartType: string): string {
	return CHART_TYPE_LABELS[chartType] ?? chartType;
}

export const CHART_TYPE_REQUIREMENTS: Record<string, ChartTypeRequirements> = {
	line: {
		timeSeries: true,
		usesCategory: false,
		minMetrics: 1,
		supportsDimensions: false,
	},
	area: {
		timeSeries: true,
		usesCategory: false,
		minMetrics: 1,
		supportsDimensions: false,
	},
	bar: {
		timeSeries: false,
		usesCategory: true,
		minMetrics: 1,
		supportsDimensions: true,
	},
	column: {
		timeSeries: false,
		usesCategory: true,
		minMetrics: 1,
		supportsDimensions: true,
	},
	donut: {
		timeSeries: false,
		usesCategory: true,
		minMetrics: 1,
		maxMetrics: 1,
		supportsDimensions: true,
	},
	scatter: {
		timeSeries: false,
		usesCategory: false,
		minMetrics: 2,
		maxMetrics: 2,
		supportsDimensions: true,
	},
	number: {
		timeSeries: false,
		usesCategory: false,
		minMetrics: 1,
		maxMetrics: 1,
		supportsDimensions: false,
	},
};

/**
 * Options for data migration.
 */
export interface DataMigrationOptions {
	/** Preserve existing filters when possible */
	preserveFilters: boolean;
	/** Strategy for handling metrics mismatch */
	metricsStrategy: 'truncate' | 'keep' | 'error';
	/** Strategy for handling dimensions mismatch */
	dimensionsStrategy: 'clear' | 'keep' | 'error';
}

/**
 * Default migration options.
 */
export const DEFAULT_MIGRATION_OPTIONS: DataMigrationOptions = {
	preserveFilters: true,
	metricsStrategy: 'truncate',
	dimensionsStrategy: 'clear',
};
